/**
 * Ionisation constants at 25 °C for the acids and bases the calculators offer as
 * shortcuts, copied from OpenStax Chemistry 2e, Appendix H (Ionization Constants of Weak
 * Acids) and Appendix I (Ionization Constants of Weak Bases). Names live in each widget's
 * strings, keyed by `id`; formulas are language-neutral.
 */

export interface WeakConstant {
  id: string;
  formula: string;
  /** Ka for an acid, Kb for a base. */
  k: number;
}

export const WEAK_ACIDS: readonly WeakConstant[] = [
  { id: "acetic", formula: "CH3CO2H", k: 1.8e-5 },
  { id: "formic", formula: "HCO2H", k: 1.8e-4 },
  { id: "hydrofluoric", formula: "HF", k: 6.4e-4 },
  { id: "nitrous", formula: "HNO2", k: 4.6e-4 },
  { id: "hypochlorous", formula: "HClO", k: 2.9e-8 },
  { id: "hydrocyanic", formula: "HCN", k: 4.9e-10 },
];

export const WEAK_BASES: readonly WeakConstant[] = [
  { id: "ammonia", formula: "NH3", k: 1.8e-5 },
  { id: "methylamine", formula: "CH3NH2", k: 4.4e-4 },
  { id: "dimethylamine", formula: "(CH3)2NH", k: 5.9e-4 },
  { id: "trimethylamine", formula: "(CH3)3N", k: 6.3e-5 },
  { id: "aniline", formula: "C6H5NH2", k: 4.3e-10 },
];

export interface Indicator {
  id: string;
  low: number;
  high: number;
}

/** Colour-change intervals from OpenStax Chemistry 2e, Figure 14.19. */
export const INDICATORS: readonly Indicator[] = [
  { id: "bromocresolGreen", low: 3.8, high: 5.4 },
  { id: "methylRed", low: 4.2, high: 6.3 },
  { id: "bromothymolBlue", low: 6.0, high: 7.6 },
  { id: "phenolRed", low: 6.8, high: 8.2 },
  { id: "phenolphthalein", low: 8.0, high: 10.0 },
  { id: "thymolphthalein", low: 9.3, high: 10.5 },
];

export interface BufferPair extends WeakConstant {
  /** The acid form of the pair, as a formula. */
  acidFormula: string;
  /** The conjugate-base form, as the salt that is weighed out. */
  baseFormula: string;
}

/**
 * Conjugate pairs common in buffers, with the Ka of the acid at 25 °C from OpenStax
 * Chemistry 2e, Appendix H — except ammonium, whose Ka is Kw ÷ Kb of ammonia
 * (1.0 × 10⁻¹⁴ ÷ 1.8 × 10⁻⁵ = 5.6 × 10⁻¹⁰).
 */
export const BUFFER_PAIRS: readonly BufferPair[] = [
  {
    id: "acetic",
    formula: "CH3CO2H",
    k: 1.8e-5,
    acidFormula: "CH3CO2H",
    baseFormula: "CH3CO2Na",
  },
  {
    id: "formic",
    formula: "HCO2H",
    k: 1.8e-4,
    acidFormula: "HCO2H",
    baseFormula: "HCO2Na",
  },
  {
    id: "carbonic",
    formula: "H2CO3",
    k: 4.3e-7,
    acidFormula: "H2CO3",
    baseFormula: "NaHCO3",
  },
  {
    id: "bicarbonate",
    formula: "HCO3-",
    k: 4.7e-11,
    acidFormula: "NaHCO3",
    baseFormula: "Na2CO3",
  },
  {
    id: "dihydrogenPhosphate",
    formula: "H2PO4-",
    k: 6.2e-8,
    acidFormula: "NaH2PO4",
    baseFormula: "Na2HPO4",
  },
  {
    id: "hydrogenPhosphate",
    formula: "HPO42-",
    k: 4.2e-13,
    acidFormula: "Na2HPO4",
    baseFormula: "Na3PO4",
  },
  {
    id: "ammonium",
    formula: "NH4+",
    k: 5.6e-10,
    acidFormula: "NH4Cl",
    baseFormula: "NH3",
  },
  {
    id: "hypochlorous",
    formula: "HClO",
    k: 2.9e-8,
    acidFormula: "HClO",
    baseFormula: "NaClO",
  },
];
